import React, { ReactNode } from "react";
import clsx from "clsx";

interface AppTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "subtitle1" | "subtitle2" | "body" | "body1" | "caption" | "overline" | "xs" | "label";
  align?: "left" | "center" | "right" | "justify";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success" | "dark" | "light" | "inherit" | "custom" | "text" | "white" | "disabled";
  weight?: "light" | "normal" | "medium" | "bold";
  noWrap?: boolean;
  gutterBottom?: boolean;
  customColor?: string;
}

const AppText: React.FC<AppTextProps> = ({
  children,
  variant = "body",
  align = "left",
  color = "dark",
  weight = "normal",
  noWrap = false,
  gutterBottom = false,
  customColor,
  className = "",
  style = {},
  ...props
}) => {
  const classes = clsx(
    "inline-flex transition-all duration-300 ease-in-out",
    {
      // Variant classes (Tailwind classes for typography sizes)
      "text-4xl font-bold": variant === "h1",
      "text-3xl font-bold": variant === "h2",
      "text-2xl font-semibold": variant === "h3",
      "text-xl font-semibold": variant === "h4",
      "text-lg font-medium": variant === "h5",
      "text-base font-medium": variant === "h6",
      "text-sm font-normal": variant === "subtitle1",
      "text-xs font-normal": variant === "subtitle2",
      "text-base": variant === "body1",
      "text-sm": variant === "body",
      "text-xs italic": variant === "caption",
      "text-xs": variant === "xs",
      "uppercase text-xs tracking-widest": variant === "overline",
      "text-sm font-medium": variant === "label",

      // Alignment classes
      "text-left": align === "left",
      "text-center": align === "center",
      "text-right": align === "right",
      "text-justify": align === "justify",

      // Color classes
      "text-primary": color === "primary",
      "text-secondary": color === "secondary",
      "text-red-600": color === "error",
      "text-yellow-600": color === "warning",
      "text-blue-600": color === "info",
      "text-green-600": color === "success",
      "text-gray-900": color === "dark",
      "text-gray-100": color === "light",
      "text-gray-400": color === "disabled",
      "text-white": color === "white",

      // Other common text-related classes
      "whitespace-nowrap": noWrap,
      "mb-2": gutterBottom,
    },
    className
  );

  const dynamicStyle = {
    ...style,
    color: color === "custom" ? customColor : undefined, // Apply custom color if specified
  };

  return (
    <div className={classes} style={dynamicStyle} {...props}>
      {children}
    </div>
  );
};

export default AppText;